/**
 * About.jsx — Luvia About Us Page
 *
 * Fixes vs submitted version:
 *
 * 1.  SITE_URL: theluviajewels.com (consistent across codebase)
 * 2.  og:image added
 * 3.  og:type: website → website (correct for AboutPage, kept)
 *     og:locale added
 * 4.  JSON-LD moved to module level — no stringify on every render
 * 5.  JSON-LD: @graph with AboutPage + BreadcrumbList + Organization ref
 *     Organization uses same @id as Story.jsx (#organization) — cross-ref
 * 6.  Two H2s inside single section → Mission & Vision are separate sections
 *     each with its own aria-labelledby
 * 7.  Self-closing <li> breadcrumb separator fixed (7th occurrence)
 * 8.  contain: layout style → contain: layout
 * 9.  .about-commitment__list li → .about-commitment__item (no element targeting)
 * 10. IconShield, IconPackage, IconCheck imports → inline SVGs at module level
 * 11. Page title improved: keyword-rich
 * 12. max-width: 1000px removed from page shell — full-width fix
 * 13. Desktop: two-column layout + sticky sidebar with metrics/trust
 * 14. about-values cards: get background + gold border-top (visible anchoring)
 * 15. about-different: rewritten with concrete differentiators + free shipping
 * 16. Primary CTA: "Read Our Story" → "Shop the Collection" (conversion fix)
 * 17. Metric strip added below hero (trust signals above the fold)
 * 18. Mission/Vision restructured: Mission = full-width, Vision = sidebar card
 */

import { Link }   from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout     from '../../components/common/Layout/Layout';
import { BRAND }  from '../../config/brand';
import './About.css';

// ── Constants ─────────────────────────────────────────────────────────────
const SITE_URL   = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://theluviajewels.com';
const BRAND_NAME = BRAND?.name || 'Luvia';
const ABOUT_URL  = `${SITE_URL}/about`;
const OG_IMAGE   = `${SITE_URL}/og-image.jpg`;
const BRAND_YEAR = 2026;

// ── Static data ───────────────────────────────────────────────────────────
const CORE_VALUES = [
  {
    id: 'quality',
    icon: 'shield',
    title: 'Quality First',
    text: 'Every piece undergoes rigorous quality checks. Premium finishes, durable materials, and attention to detail in every design.',
  },
  {
    id: 'sustainable',
    icon: 'leaf',
    title: 'Responsible Making',
    text: 'From material sourcing to packaging, every choice respects our planet. Ethical suppliers, minimal waste, long-lasting jewelry.',
  },
  {
    id: 'customer',
    icon: 'heart',
    title: 'Customer First',
    text: "Your satisfaction shapes our decisions — from sizing to shipping. We listen, learn, and improve with every order we fulfil.",
  },
  {
    id: 'innovation',
    icon: 'layers',
    title: 'Timeless Design',
    text: "We don't follow micro trends. We create refined essentials - rings, earrings, necklaces, and bracelets designed to stay in your collection.",
  },
];

// Concrete, specific differentiators (not abstract brand positioning)
const DIFFERENTIATORS = [
  {
    id: 'materials',
    title: 'Premium Materials & Finishes',
    text: 'Our jewelry uses brass, alloy, and copper with gold, rose gold, silver, and gunmetal plating. Every piece is crafted for everyday elegance and long wear.',
  },
  {
    id: 'shipping',
    title: 'Free Shipping Above 500',
    text: 'We offer free shipping on orders above 500. We dispatch within 1-2 business days with full tracking from our warehouse to your door.',
  },
  {
    id: 'returns',
    title: 'No Returns (For Now)',
    text: 'Returns are currently unavailable while we refine our operations. Please review product details and size guidance before placing your order.',
  },
  {
    id: 'direct',
    title: 'Direct to Customer',
    text: 'No middlemen, no retail markup. By selling directly we keep prices fair and stay close to the people wearing our jewelry.',
  },
];

const COMMITMENTS = [
  { id: 'qc',      text: <><strong>Premium quality checks</strong> — every product inspected before it ships.</> },
  { id: 'transp',  text: <><strong>Transparent practices</strong> — honest about materials, processes, and pricing.</> },
  { id: 'support', text: <><strong>Responsive support</strong> — real help via WhatsApp or email, fast.</> },
  { id: 'improve', text: <><strong>Continuous improvement</strong> — your feedback shapes our next decisions.</> },
  { id: 'sustain', text: <><strong>Sustainable future</strong> — a brand you can feel good about supporting.</> },
];

// Sidebar metrics — trust signals above the fold on desktop
const METRICS = [
  { id: 'year',     value: `Est. ${BRAND_YEAR}`, label: 'Brand founded' },
  { id: 'dispatch', value: '48h',                label: 'Dispatch time' },
  { id: 'shipping', value: '500+',               label: 'Free shipping threshold' },
  { id: 'returns',  value: 'No',                 label: 'Returns currently unavailable' },
];

// ── Structured data — module level, serialized once ───────────────────────
const PAGE_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'AboutPage',
      '@id': `${ABOUT_URL}#webpage`,
      url: ABOUT_URL,
      name: `About Us | ${BRAND_NAME}`,
      description: `Learn about ${BRAND_NAME} — a premium women's artificial jewelry brand founded in ${BRAND_YEAR}, dedicated to elegant, everyday jewelry.`,
      inLanguage: 'en-IN',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      breadcrumb: { '@id': `${ABOUT_URL}#breadcrumb` },
      mainEntity: { '@id': `${SITE_URL}/#organization` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id': `${ABOUT_URL}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'About Us', item: ABOUT_URL },
      ],
    },
    {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: BRAND_NAME,
      url: SITE_URL,
      logo: `${SITE_URL}/logo.png`,
      foundingDate: String(BRAND_YEAR),
      description: `${BRAND_NAME} is a premium women's artificial jewelry brand specialising in elegant rings, earrings, necklaces, and more. Founded in ${BRAND_YEAR}.`,
      sameAs: [BRAND?.instagram].filter(Boolean),
    },
  ],
});

// ── Inline SVG icons — no import cost ────────────────────────────────────
function IconShield() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
    </svg>
  );
}

function IconLeaf() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <path d="M17 8C8 10 5.9 16.17 3.82 19.31a1.02 1.02 0 0 0 1.43 1.4C7.7 18.5 12 17 17 8z"/>
      <path d="M17 8c.9 6.5-5 9-9 11"/>
    </svg>
  );
}

function IconHeart() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
    </svg>
  );
}

function IconLayers() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <polygon points="12 2 2 7 12 12 22 7 12 2"/>
      <polyline points="2 17 12 22 22 17"/>
      <polyline points="2 12 12 17 22 12"/>
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function IconArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12 5 19 12 12 19"/>
    </svg>
  );
}

const VALUE_ICONS = { shield: IconShield, leaf: IconLeaf, heart: IconHeart, layers: IconLayers };

// ── Component ──────────────────────────────────────────────────────────────
export default function About() {
  const pageTitle = `About Us | ${BRAND_NAME}`;
  const pageDesc  = `Learn about ${BRAND_NAME} — a premium women's artificial jewelry brand founded in ${BRAND_YEAR}, dedicated to elegant everyday jewelry.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={ABOUT_URL} />

        <link rel="prefetch" href="/shop" as="document" />
        <link rel="prefetch" href="/story" as="document" />
        <link rel="prefetch" href="/contact" as="document" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={ABOUT_URL} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`About ${BRAND_NAME} — Women's Artificial Jewelry`} />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content={`About ${BRAND_NAME} — Premium Indian Fashion`} />

        <script type="application/ld+json">{PAGE_SCHEMA}</script>
      </Helmet>

      <Layout showFooter showBottomTab>
        <main className="about-page" id="main-content">

          {/* ── Full-width dark hero ───────────────────────────────────── */}
          <div className="about-hero">
            <div className="about-hero__inner">

              {/* Breadcrumb */}
              <nav className="about-breadcrumb" aria-label="Breadcrumb">
                <ol className="about-breadcrumb__list">
                  <li>
                    <Link to="/" className="about-breadcrumb__link">Home</Link>
                  </li>
                  {/* Valid non-void <li> — fixed from self-closing void element */}
                  <li aria-hidden="true" className="about-breadcrumb__sep">
                    <span>›</span>
                  </li>
                  <li>
                    <span aria-current="page" className="about-breadcrumb__current">
                      About Us
                    </span>
                  </li>
                </ol>
              </nav>

              <p className="about-hero__eyebrow" aria-hidden="true">
                Est. {BRAND_YEAR}
              </p>
              <h1 className="about-hero__title">
                About {BRAND.name}
              </h1>
              <p className="about-hero__subtitle">
                A luxury jewelry house built for modern women. Born in {BRAND_YEAR} with one belief -
                fine design, responsible making, and trust-led service should always go together.
              </p>

            </div>

            {/* Metric strip — trust signals above the fold */}
            <div className="about-metrics">
              <div className="about-metrics__inner">
                {METRICS.map(({ id, value, label }) => (
                  <div key={id} className="about-metric">
                    <span className="about-metric__value">{value}</span>
                    <span className="about-metric__label">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Two-column body layout ─────────────────────────────────── */}
          <div className="about-layout">

            {/* ── Sticky sidebar (desktop only) ─────────────────────────── */}
            <aside className="about-sidebar" aria-label="Brand commitments">
              <div className="about-sidebar__inner">
                <p className="about-sidebar__label">Our commitments</p>
                <ul className="about-sidebar__list" role="list">
                  {COMMITMENTS.map(({ id, text }) => (
                    <li key={id} className="about-sidebar__item">
                      <span className="about-sidebar__icon" aria-hidden="true">
                        <IconCheck />
                      </span>
                      <span className="about-sidebar__text">{text}</span>
                    </li>
                  ))}
                </ul>
                <div className="about-sidebar__divider" />
                <Link to="/shop" className="about-sidebar__cta">
                  Shop →
                </Link>
              </div>
            </aside>

            {/* ── Main content ──────────────────────────────────────────── */}
            <div className="about-main">

              {/* ── Mission ────────────────────────────────────────────── */}
              {/*
                Fixed: previously two H2s inside one section with one
                aria-labelledby. Now Mission and Vision are separate sections,
                each with their own heading and labelledby.
              */}
              <section className="about-section" aria-labelledby="mission-h">
                <h2 id="mission-h" className="about-section__title">Our Mission</h2>
                <div className="about-section__content">
                  <p className="about-section__paragraph">
                    At {BRAND.name}, we believe jewelry should be both beautiful and responsible.
                    Our mission is to create premium, thoughtfully designed pieces that stand the
                    test of time — reducing waste, honouring craftsmanship, bringing you jewelry
                    you'll reach for every day, for years.
                  </p>
                  <p className="about-section__paragraph">
                    We're not just another fashion brand. We're a movement toward conscious
                    consumption — where every piece tells a story of quality, care, and commitment
                    to getting it right.
                  </p>
                </div>
              </section>

              {/* ── Vision ─────────────────────────────────────────────── */}
              <section className="about-section about-section--highlight" aria-labelledby="vision-h">
                <h2 id="vision-h" className="about-section__title">Our Vision</h2>
                <div className="about-section__content">
                  <p className="about-section__paragraph">
                    To become India's most trusted premium jewelry brand — known not just for our
                    designs, but for our unwavering commitment to quality, sustainability, and
                    customer satisfaction.
                  </p>
                  <p className="about-section__paragraph">
                    A future where jewelry is sustainable by default. Where quality triumphs over
                    quantity. Where every customer feels valued, heard, and confident in what
                    they wear.
                  </p>
                </div>
              </section>

              {/* ── Core Values ────────────────────────────────────────── */}
              <section className="about-section" aria-labelledby="values-h">
                <h2 id="values-h" className="about-section__title">Core Values</h2>
                {/*
                  Values now have background + gold border-top (matches SizeGuide
                  FitCards for consistency). Previously floated with no visual
                  anchoring — users skipped them.
                  4-col only at 1200px+ (was 1024px — too narrow for readable cards).
                */}
                <div className="about-values">
                  {CORE_VALUES.map(({ id, icon, title, text }) => {
                    const Icon = VALUE_ICONS[icon];
                    return (
                      <div key={id} className="about-value">
                        <div className="about-value__icon" aria-hidden="true">
                          <Icon />
                        </div>
                        <h3 className="about-value__title">{title}</h3>
                        <p  className="about-value__text">{text}</p>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* ── What Makes Us Different ────────────────────────────── */}
              {/*
                Rewritten with concrete, specific differentiators.
                Previous: abstract brand positioning ("Thoughtful Design",
                "Innovation") — same ground as Values section.
                Now: specific facts customers actually care about:
                material quality, free shipping over 500, current no-return
                policy, direct-to-customer pricing. Adds conversion value.
              */}
              <section className="about-section about-section--dark" aria-labelledby="different-h">
                <h2 id="different-h" className="about-section__title about-section__title--light">
                  What Makes Us Different
                </h2>
                <div className="about-different">
                  {DIFFERENTIATORS.map(({ id, title, text }) => (
                    <div key={id} className="about-different__item">
                      <h3 className="about-different__title">{title}</h3>
                      <p  className="about-different__text">{text}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* ── Mobile-only commitments (sidebar on desktop) ──────── */}
              <section className="about-section about-commitment-mobile" aria-labelledby="commit-h">
                <h2 id="commit-h" className="about-section__title">Our Commitment to You</h2>
                <ul className="about-commitment__list" role="list">
                  {COMMITMENTS.map(({ id, text }) => (
                    <li key={id} className="about-commitment__item">
                      <span className="about-commitment__icon" aria-hidden="true">
                        <IconCheck />
                      </span>
                      <span className="about-commitment__text">{text}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* ── CTA ────────────────────────────────────────────────── */}
              <section className="about-cta" aria-labelledby="cta-h">
                <h2 id="cta-h" className="about-cta__title">Join Us on This Journey</h2>
                <p className="about-cta__text">
                  Discover premium pieces crafted to elevate your everyday look, with free shipping
                  on orders above 500.
                </p>
                <div className="about-cta__actions">
                  <Link to="/shop" className="about-cta__btn about-cta__btn--primary">
                    Shop
                    <IconArrowRight />
                  </Link>
                  <Link to="/story" className="about-cta__btn about-cta__btn--ghost">
                    Read Our Story
                    <IconArrowRight />
                  </Link>
                </div>
              </section>

              {/* ── Related (internal linking) ───────────────────────────── */}
              <nav className="about-related" aria-label="Related information">
                <h2 className="about-related__title">Related information</h2>
                <ul className="about-related__list" role="list">
                  <li><Link to="/story" className="about-related__link">Our Story</Link></li>
                  <li><Link to="/contact" className="about-related__link">Contact Us</Link></li>
                  <li><Link to="/shipping" className="about-related__link">Shipping &amp; Delivery</Link></li>
                  <li><Link to="/faq" className="about-related__link">FAQ</Link></li>
                  <li><Link to="/shop" className="about-related__link">Shop All</Link></li>
                </ul>
              </nav>

            </div>{/* /about-main */}
          </div>{/* /about-layout */}

        </main>
      </Layout>
    </>
  );
}